import { toastController } from '@ionic/vue'
import type { AuthStore, JsonRpcPayload, TokenHandshakeHandler } from './types'
import {
	AP_CODE_NOT_IN_LZ_MESSAGE,
	findLzAccessByApCode,
	parseApCodesFromParams,
	persistPendingApCodeToast,
	type LzApProvider
} from './find-lz-access-by-ap-code'

export const NO_PROVIDER_MESSAGE = 'Nemáte priradeného žiadneho poskytovateľa'

type AccessResolutionMode = 'preserve' | 'fallback'

type ApplyAccessOptions = {
	mode: AccessResolutionMode
	logoutOnMismatch?: boolean
}

function clearHandshakeAuth(authStore: { logout: (refresh?: boolean) => unknown }) {
	localStorage.removeItem('mdx_kz_user_token')
	localStorage.removeItem('mdx_kz_user_user')
	localStorage.removeItem('mdx_kz_current_access')
	localStorage.removeItem('mdx_kz_current_provider')
	authStore.logout(false)
}

function resolveFallbackAccess(authStore: AuthStore) {
	const providers = authStore.getProviders as LzApProvider[] | null
	const firstProvider = providers?.[0]

	const lastAccessStorage = authStore.getLastAccess as { id?: number | string } | null
	const lastProviderStorage = authStore.getLastProvider as { id?: number | string } | null

	const lastProvider = providers?.find(
		(provider) => provider.id === lastProviderStorage?.id
	)
	const lastAccess = lastProvider?.accesses?.find(
		(access) => access.id === lastAccessStorage?.id
	)

	if (lastAccess && lastProvider) {
		return { access: lastAccess, provider: lastProvider }
	}

	if (!firstProvider?.accesses?.length) return null

	return { access: firstProvider.accesses[0], provider: firstProvider }
}

async function showApCodeMismatchToast() {
	const toast = await toastController.create({
		message: AP_CODE_NOT_IN_LZ_MESSAGE,
		color: 'danger',
		position: 'top',
		duration: 5000,
		cssClass: 'w-toast w-top'
	})
	await toast.present()
}

async function apCodeMismatchError(
	authStore: AuthStore,
	logoutOnMismatch: boolean
): Promise<JsonRpcPayload> {
	// Persist first — CURO často reloadne iframe hneď po JSON-RPC error reply.
	if (logoutOnMismatch) {
		persistPendingApCodeToast()
	}

	try {
		await showApCodeMismatchToast()
	} catch (error) {
		console.error('[token-handshake] Failed to present AP mismatch toast', error)
	}

	if (logoutOnMismatch) {
		clearHandshakeAuth(authStore)
	}

	return {
		error: {
			code: -32600,
			message: AP_CODE_NOT_IN_LZ_MESSAGE
		}
	}
}

function noProviderError(
	authStore: AuthStore,
	logoutOnMismatch: boolean
): JsonRpcPayload {
	if (logoutOnMismatch) {
		clearHandshakeAuth(authStore)
	}

	return {
		error: {
			code: -32600,
			message: NO_PROVIDER_MESSAGE
		}
	}
}

/**
 * Nastaví current access podľa doctorCode/providerCode (alebo a_code/p_code).
 * providerCode sa matchuje na ambulance.pzs_id, doctorCode na access.a_code/code.
 * - preserve: bez AP kódov ponechá aktuálny access
 * - fallback: bez AP kódov použije last/first access
 */
export async function applyAccessFromParams(
	params: Record<string, unknown> | undefined,
	authStore: AuthStore,
	options: ApplyAccessOptions
): Promise<JsonRpcPayload | null> {
	const providers = authStore.getProviders as LzApProvider[] | null
	const logoutOnMismatch = options.logoutOnMismatch ?? false

	if (!providers?.[0]?.accesses?.length) {
		return noProviderError(authStore, logoutOnMismatch)
	}

	const apCodes = parseApCodesFromParams(params)

	console.log('[token-handshake] AP codes from CURO', {
		doctorCode: params?.doctorCode ?? params?.a_code ?? null,
		providerCode: params?.providerCode ?? params?.p_code ?? null,
		parsed: apCodes,
		mode: options.mode
	})

	if (apCodes) {
		const matched = findLzAccessByApCode(providers, apCodes.aCode, apCodes.pCode)

		if (!matched) {
			return apCodeMismatchError(authStore, logoutOnMismatch)
		}

		authStore.setLzAccess({
			access: matched.access,
			provider: matched.provider
		})

		return null
	}

	if (options.mode === 'preserve') {
		return authStore.getCurrentAccess ? null : noProviderError(authStore, logoutOnMismatch)
	}

	const fallback = resolveFallbackAccess(authStore)
	if (!fallback?.access) {
		return noProviderError(authStore, logoutOnMismatch)
	}

	authStore.setLzAccess(fallback)

	return null
}

export function withAccessContext(
	handler: TokenHandshakeHandler,
	options: ApplyAccessOptions = { mode: 'preserve' }
): TokenHandshakeHandler {
	return async (params, ctx) => {
		const accessError = await applyAccessFromParams(params, ctx.authStore, options)
		if (accessError) return accessError

		return handler(params, ctx)
	}
}
